import { useCallback, useEffect, useState } from 'react';
import { TreeViewBy } from '../../types';
import { distinctArray } from '../../utils/common';

type TreeNode = {
  id: string;
  parentId?: string;
};

function useExpandedTreeNodes(
  treeViewBy: TreeViewBy,
  topLevelFolders: string[],
  search: string,
  folderNodeIds: string[],
  selectedTreeNode: TreeNode,
) {
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  const expandTreeNode = useCallback(
    (treeNode: TreeNode, collapseExisting?: boolean) => {
      const foldersToExpand = folderNodeIds.filter(
        (path) => treeNode.id.includes(`${path}/`) || treeNode.id === path,
      );

      setExpandedNodes((prev) =>
        collapseExisting
          ? distinctArray(foldersToExpand)
          : distinctArray([...prev, ...foldersToExpand]),
      );
    },
    [folderNodeIds],
  );

  const expandDefaultNodes = useCallback(() => {
    if (topLevelFolders.length === 1) {
      setExpandedNodes([topLevelFolders[0]]);
    } else {
      setExpandedNodes([]);
    }
  }, [topLevelFolders]);

  useEffect(() => {
    if (topLevelFolders.length > 0 && folderNodeIds.length > 0 && !isLoaded) {
      expandDefaultNodes();
      setIsLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topLevelFolders, folderNodeIds, isLoaded]);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    expandDefaultNodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeViewBy]);

  useEffect(() => {
    if (!selectedTreeNode) return;
    const isFolder = selectedTreeNode.parentId === undefined;
    const nodeToExpand = isFolder
      ? selectedTreeNode.id
      : selectedTreeNode.parentId;

    if (nodeToExpand) {
      expandTreeNode({ id: nodeToExpand });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTreeNode]);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    if (search.trim() !== '') {
      setExpandedNodes(folderNodeIds);
      return;
    }

    if (search.trim() === '') {
      if (selectedTreeNode) {
        const isFolder = selectedTreeNode.parentId === undefined;
        const nodeToExpand = isFolder
          ? selectedTreeNode.id
          : selectedTreeNode.parentId;
        if (nodeToExpand) {
          expandTreeNode({ id: nodeToExpand }, true);
        }
      } else {
        expandDefaultNodes();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return { expandedNodes, setExpandedNodes };
}

export default useExpandedTreeNodes;
